import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { FiCamera, FiUser } from 'react-icons/fi';
import api from '../services/api';

const Register = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    is_host: false,
    is_owner: false,
  });
  const [loading, setLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let avatarUrl = null;

      // Upload avatar if selected
      if (avatarFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', avatarFile);

        const uploadResponse = await api.post('/api/uploads/image?folder=avatars', formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        avatarUrl = uploadResponse.data.image_url;
      }

      // Register user with avatar URL
      const registrationData = { ...formData };
      if (avatarUrl) {
        registrationData.avatar = avatarUrl;
      }

      const result = await register(registrationData);

      if (result.success) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error during registration:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-light min-vh-100 d-flex align-items-center py-5" style={{ marginTop: '70px' }}>
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-4 p-md-5">
                <div className="text-center mb-4">
                  <h1 className="h3 fw-bold text-nukki-dark">{t('auth.createAccount')}</h1>
                  <p className="text-muted">{t('auth.registerSubtitle')}</p>
                </div>

                <Form onSubmit={handleSubmit}>
                  {/* Avatar Upload */}
                  <Form.Group className="mb-4 text-center">
                    <Form.Label className="fw-medium d-block mb-3">{t('auth.profilePhoto')}</Form.Label>
                    <div className="d-flex flex-column align-items-center gap-3">
                      <div className="position-relative" style={{ width: '120px', height: '120px' }}>
                        {avatarPreview ? (
                          <img
                            src={avatarPreview}
                            alt="Avatar preview"
                            className="rounded-circle w-100 h-100"
                            style={{
                              objectFit: 'cover',
                              border: '3px solid var(--bs-border-color)'
                            }}
                          />
                        ) : (
                          <div
                            className="rounded-circle w-100 h-100 bg-light d-flex align-items-center justify-content-center"
                            style={{ border: '3px solid var(--bs-border-color)' }}
                          >
                            <FiUser size={48} className="text-muted" />
                          </div>
                        )}
                        <Form.Label
                          htmlFor="avatar-upload"
                          className="position-absolute bottom-0 end-0 bg-danger rounded-circle d-flex align-items-center justify-content-center mb-0"
                          style={{
                            width: '36px',
                            height: '36px',
                            cursor: 'pointer',
                            border: '3px solid white'
                          }}
                        >
                          <FiCamera size={18} color="white" />
                        </Form.Label>
                        <Form.Control
                          id="avatar-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          className="d-none"
                        />
                      </div>
                      {avatarPreview && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleRemoveAvatar}
                        >
                          {t('auth.removePhoto')}
                        </Button>
                      )}
                      <Form.Text className="text-muted">
                        {t('auth.photoOptional')}
                      </Form.Text>
                    </div>
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-medium">{t('auth.firstName')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-medium">{t('auth.lastName')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">{t('auth.email')}</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('auth.emailPlaceholder')}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">{t('auth.phone')}</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t('auth.phonePlaceholder')}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">{t('auth.password')}</Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder={t('auth.passwordPlaceholder')}
                      required
                      minLength="6"
                    />
                    <Form.Text className="text-muted">
                      {t('auth.passwordMinLength')}
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      name="is_host"
                      id="is_host"
                      checked={formData.is_host}
                      onChange={handleChange}
                      label={t('auth.wantToList')}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Check
                      type="checkbox"
                      name="is_owner"
                      id="is_owner"
                      checked={formData.is_owner}
                      onChange={handleChange}
                      label={t('auth.propertyOwner', 'Sou proprietário de imóvel')}
                    />
                    <Form.Text className="text-muted d-block mt-1">
                      {t('auth.propertyOwnerHint', 'Proprietários podem acompanhar receita e ocupação dos seus imóveis')}
                    </Form.Text>
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-100"
                    disabled={loading}
                  >
                    {loading ? t('auth.creatingAccount') : t('auth.createAccountBtn')}
                  </Button>
                </Form>

                <div className="text-center mt-4">
                  <p className="mb-0 text-muted">
                    {t('auth.alreadyHaveAccount')}{' '}
                    <Link to="/login" className="text-primary text-decoration-none fw-medium">
                      {t('auth.loginHere')}
                    </Link>
                  </p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Register;
